import React from "react";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import AlbumSymptoms from "../album/albumSymptoms";
import TemporaryClinicBookingDialog from "./TemporaryClinicBookingDialog";
import Assessment2 from "../images/assessment2.png";
import program from "../images/program.png";
import weeklyphysiocheckin from "../images/weeklyphysiocheckin.png";
import Screens from "../images/screens.jpg";
import CEMark from "../images/CE_mark.png";
import GDPRcompliant_logoGrey from "../images/GDPR-compliant-logo-Grey.png";
import DanishHealth from "../images/Danish_Health_Authority.png";
import hybridPhysio4148919 from "../images/hybridPhysio4148919.png";
import { LANDING_LAPTOP_BAND, LANDING_SHARED_BUTTONS, LANDING_SHARED_FONTS, LANDING_SHARED_LAYOUT, LANDING_SHARED_TYPOGRAPHY } from "./sharedStyles";

function goToAssessment() {
  window.location.replace("/virtual_consultation");
}

const HERO_VIDEO_MP4_SRC = `${process.env.PUBLIC_URL || ""}/media/landing-hero.mp4`;
const HERO_VIDEO_WEBM_SRC = `${process.env.PUBLIC_URL || ""}/media/landing-hero.webm`;

function getFindClinicLabel() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(min-width: 960px)").matches ? "Find a clinic near you" : "Find clinic";
  }

  return "Find a clinic near you";
}

function getStartIntakeLabel() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(min-width: 960px)").matches ? "Start Digital Intake" : "START INTAKE";
  }

  return "Start Digital Intake";
}

const stats = [
  { value: "< 5-10 min", label: "to complete your digital intake" },
  { value: "Match & Book", label: "the right physiotherapist for your needs, location and schedule" },
  { value: "Free ~20 min.", label: (<>for the physio to focus on you on the <b>first appointment</b></>)},
  { value: "And ~10 min.", label: (<>on <b>follow-up appointments</b> with feedback & check-ins</>)},
];

const compactStatsLabels = [
  "to complete your intake",
  "the right physio for your needs and schedule",
  (<>for the physio to focus on you on the <b>first appointment</b></>),
  (<>on <b>follow-up appointments</b> with feedback</>),
];

const xsStatsLabels = [
  "complete in minutes",
  "matched to the right physio",
  (<>focus on your <b>first appointment</b></>),
  (<>follow-up <b>check-ins</b></>),
];

const howItWorksSteps = [
  {
    id: "step-1",
    number: 1,
    image: Assessment2,
    alt: "Digital intake preview",
    title: "Complete your digital intake",
    text: "Answer a short guided questionnaire from home. Describe your concern, how it affects daily life, and what you want help with in less than five minutes.",
  },
  {
    id: "step-2",
    number: 2,
    image: program,
    alt: "Clinic matching and booking preview",
    title: "Get matched and choose your clinic path",
    text: "Start with intake first or find a clinic first. Either way, the right physiotherapist gets the right context before the first appointment.",
  },
  {
    id: "step-3",
    number: 3,
    image: weeklyphysiocheckin,
    alt: "Weekly follow-up preview",
    title: "Follow your plan between visits",
    text: "Your physiotherapist can keep rehabilitation moving between appointments with home exercises, weekly check-ins, and remote follow-up when needed.",
  },
];
const patientFeatures = [
  {
    id: "feature-intake",
    tag: "Digital Intake · Step 1",
    image: Assessment2,
    alt: "Digital intake form",
    title: "Share your story before you even walk through the door",
    bullets: [
      "Complete a guided intake from home and describe where it hurts, how it started, and what matters most to you.",
      "Your physiotherapist receives a fuller picture before the first appointment, so session time can focus on treatment instead of paperwork.",
      "Smart matching helps connect you to the right specialist at a clinic near you based on your area of concern.",
    ],
    ctaLabel: "Start your intake",
  },
  {
    id: "feature-program",
    tag: "Your Exercise Plan · Step 2",
    image: program,
    alt: "Personalised home exercise programme",
    title: "Your personalised home programme, designed by your physio and done on your terms",
    bullets: [
      "Your physiotherapist can build your exercise plan directly in the platform around your needs and goals.",
      "Step-by-step guided exercises in the patient app make it easier to stay on track between clinic visits.",
      "Rate sessions and add notes so your physio can understand how rehabilitation is going between appointments.",
    ],
    ctaLabel: "Start digital intake",
  },
  {
    id: "feature-followup",
    image: weeklyphysiocheckin,
    alt: "Weekly check-in and remote monitoring",
    title: "Stay connected to your physio between appointments, not just on the day",
    bullets: [
      "Weekly check-ins keep your physiotherapist informed about your progress and how you are feeling.",
      "Small plan adjustments can happen around your feedback without forcing an extra appointment for every tweak.",
      "Progress visibility makes rehabilitation feel more joined-up, motivating, and easier to stick with.",
    ],
    ctaLabel: "See how follow-ups work",
  },
];

const physioBonuses = [
  {
    id: "bonus-time",
    icon: "⏱️",
    title: "More time for what matters",
    text: "Be fully prepared when the patient arrives. No more spending the first 20 minutes on routine history-taking — free up time to focus on the patient.",
  },
  {
    id: "bonus-intake",
    icon: "📋",
    title: "Structured intake, every time",
    text: "A consistent digital intake means you get the same quality of information from every patient — reliable, no surprises, no hallucinations.",
  },
  {
    id: "bonus-match",
    icon: "🎯",
    title: "Patient matching grows your clinic",
    text: "Get matched with the right patients for your clinic and receive qualified leads, while saving time on appointments and improving patient retention.",
  },
  {
    id: "bonus-plan",
    icon: "📱",
    title: "Prescribe adaptive plans - you control",
    text: "Build and assign adaptive exercise programmes directly in the platform. Patients follow guided sessions in their app — while you stay in full control and updated.",
  },
  {
    id: "bonus-track",
    icon: "📊",
    title: "Remote progress monitoring",
    text: "Track patient adherence and self-reported outcomes between sessions. Spot who needs support before they disengage from rehab.",
  },
  {
    id: "bonus-flow",
    icon: "🔁",
    title: "Weekly touchpoints, no extra effort",
    text: "Automated weekly check-ins keep patients engaged and write the journal on follow-ups based on data-driven insights — without adding a single thing to your admin load.",
  },
];

const styles = (theme) => ({
  root: {
    width: "100%",
    color: "#001e3c",
    overflowX: "hidden",
    fontFamily: LANDING_SHARED_FONTS.body,
  },
  heroSection: {
    background: "linear-gradient(135deg, #001e3c 0%, #003870 55%, #0057a8 100%)",
    overflow: "hidden",
    padding: "88px 6% 84px",
    position: "relative",
    [theme.breakpoints.up("lg")]: {
      minHeight: LANDING_SHARED_LAYOUT.heroDesktopMinHeight,
      display: "flex",
      alignItems: "center",
    },
    [theme.breakpoints.down("md")]: {
      padding: "65px 5% 65px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "65px 5% 65px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "40px 12px 47px",
    },
    [LANDING_LAPTOP_BAND]: {
      padding: LANDING_SHARED_LAYOUT.heroLaptopPadding,
    },
  },
  heroOrb: {
    position: "absolute",
    top: -120,
    right: "8%",
    width: 520,
    height: 520,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,87,168,0.35) 0%, transparent 70%)",
    pointerEvents: "none",
    animation: "$pulse 3.5s ease-in-out infinite",
    [theme.breakpoints.down("xs")]: {
      width: 320,
      height: 320,
      right: -60,
      top: -40,
    },
  },
  heroInner: {
    width: "100%",
    maxWidth: LANDING_SHARED_LAYOUT.heroDesktopMaxWidth,
    margin: "0 auto",
    [LANDING_LAPTOP_BAND]: {
      maxWidth: LANDING_SHARED_LAYOUT.heroLaptopMaxWidth,
    },
  },
  heroCopyWrap: {
    position: "relative",
    zIndex: 1,
    maxWidth: 640,
    width: "100%",
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: {
      order: 2,
    },
    [LANDING_LAPTOP_BAND]: {
      maxWidth: LANDING_SHARED_LAYOUT.heroCopyLaptopMaxWidth,
    },
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.45)",
    borderRadius: 100,
    padding: "6px 16px",
    marginBottom: 24,
    color: "#c8dff7",
    fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextDesktop,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    [theme.breakpoints.down("xs")]: {
      display: "inline-flex",
      width: "auto",
      maxWidth: "100%",
      margin: "0 auto 24px",
      textAlign: "center",
    },
  },
  heroHeadline: {
    color: "#fff",
    fontWeight: 800,
    lineHeight: 1.15,
    marginBottom: 20,
    fontSize: LANDING_SHARED_TYPOGRAPHY.heroTitleDesktop,
    maxWidth: 620,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.heroTitleTablet,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.heroTitleMobile,
      lineHeight: 1.1,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.heroTitleLaptop,
      marginBottom: 12,
      maxWidth: 500,
    },
  },
  heroHeadlineAccent: {
    color: "#fb741a",
  },
  heroSub: {
    color: "#b8d4f0",
    fontSize: LANDING_SHARED_TYPOGRAPHY.heroBodyTablet,
    lineHeight: 1.7,
    maxWidth: 520,
    marginBottom: 24,
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.heroBodyMobile,
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.heroBodyLaptop,
      maxWidth: 430,
      marginBottom: 16,
    },
  },
  dualCta: {
    display: "flex",
    alignItems: "center",
    gap: LANDING_SHARED_BUTTONS.desktopGap,
    flexWrap: "wrap",
    [theme.breakpoints.down("md")]: {
      gap: LANDING_SHARED_BUTTONS.desktopGap - 5,
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "nowrap",
    },
  },
  primaryButton: {
    background: "#fb741a",
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "16px 36px",
    borderRadius: LANDING_SHARED_BUTTONS.radius,
    boxShadow: "0 8px 32px rgba(251,116,26,0.45)",
    transition: "transform 0.18s, background 0.18s, box-shadow 0.18s",
    [theme.breakpoints.down("md")]: {
      padding: LANDING_SHARED_BUTTONS.laptopPadding,
      fontSize: LANDING_SHARED_BUTTONS.desktopFontSize,
    },
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0,
      width: "auto",
      padding: "10px 12px",
      fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextMobile,
    },
    "&:hover": {
      background: "#e05e00",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 40px rgba(251,116,26,0.55)",
    },
  },
  outlineLightButton: {
    color: "#fff",
    border: "1.5px solid rgba(255,255,255,0.45)",
    fontWeight: 600,
    fontSize: "1rem",
    padding: "16px 36px",
    borderRadius: LANDING_SHARED_BUTTONS.radius,
    background: "transparent",
    textTransform: "none",
    transition: "background 0.18s, transform 0.18s, box-shadow 0.18s",
    [theme.breakpoints.down("md")]: {
      padding: LANDING_SHARED_BUTTONS.laptopPadding,
      fontSize: LANDING_SHARED_BUTTONS.desktopFontSize,
    },
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0,
      width: "auto",
      padding: "10px 12px",
      fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextMobile,
    },
    "&:hover": {
      background: "rgba(255,255,255,0.10)",
      borderColor: "rgba(255,255,255,0.45)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 40px rgba(0,40,90,0.18)",
    },
  },
  outlineBlueButton: {
    color: "#0057a8",
    border: "1.5px solid #0057a8",
    fontWeight: 600,
    fontSize: "0.95rem",
    padding: "14px 28px",
    borderRadius: LANDING_SHARED_BUTTONS.radius,
    background: "transparent",
    textTransform: "none",
    [theme.breakpoints.down("md")]: {
      padding: "12px 20px",
      fontSize: LANDING_SHARED_BUTTONS.tabletFontSize,
    },
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0,
      width: "auto",
      padding: "11px 16px",
    },
    "&:hover": {
      background: "#e8f0fc",
      borderColor: "#0057a8",
    },
  },
  heroHint: {
    color: "#7ba7d4",
    fontSize: "0.82rem",
    marginTop: 14,
    marginBottom: 28,
  },
  trustBadges: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    marginTop: 28,
    flexWrap: "wrap",
  },
  trustLabel: {
    color: "#7ba7d4",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  trustBadgeImage: {
    height: 36,
    width: "auto",
    opacity: 0.9,
    filter: "brightness(1.35)",
  },
  trustBadgeImageLarge: {
    height: 30,
    width: "auto",
    opacity: 0.9,
    filter: "brightness(1.3)",
  },
  heroIllustrationWrap: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      order: 1,
      marginTop: 0,
      marginBottom: 32,
    },
    [theme.breakpoints.down("md")]: {
      marginTop: 40,
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: 40,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      marginTop: 14,
    },
  },
  heroMediaShell: {
    position: "relative",
    width: "100%",
    maxWidth: 700,
    minHeight: 440,
    borderRadius: 34,
    overflow: "hidden",
    background: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.02) 100%)",
    boxShadow: "0 28px 72px rgba(0,0,0,0.30)",
    border: "1px solid rgba(255,255,255,0.08)",
    [theme.breakpoints.down("md")]: {
      maxWidth: 470,
      minHeight: 255,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      maxWidth: 560,
      minHeight: 340,
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 195,
      borderRadius: 18,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 2,
      pointerEvents: "none",
      background: "linear-gradient(135deg, rgba(0,30,60,0.02) 0%, rgba(0,30,60,0.22) 100%)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 3,
      pointerEvents: "none",
      background: "radial-gradient(circle at center, rgba(0,0,0,0) 42%, rgba(0,30,60,0.18) 76%, rgba(0,30,60,0.6) 100%)",
    },
  },
  heroMediaFallback: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    transition: "opacity 0.35s ease",
  },
  heroVideo: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.04)",
    filter: "saturate(1.05) contrast(1.04) brightness(0.95)",
  },
  heroIllustration: {
    width: "100%",
    maxWidth: 680,
    height: "auto",
    filter: "drop-shadow(0 24px 48px rgba(0,0,0,0.35))",
    transform: "scale(0.88) translateY(-24px)",
    [theme.breakpoints.down("md")]: {
      maxWidth: 420,
      transform: "none",
    },
  },
  statsOuter: {
    padding: "0 6%",
    marginTop: -28,
    position: "relative",
    zIndex: 10,
    [theme.breakpoints.down("md")]: {
      padding: "0 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "0 4.5%",
      marginTop: -28,
    },
    [theme.breakpoints.down("xs")]: {
      padding: "0 12px",
      marginTop: -10,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "0 5%",
    },
  },
  statsBar: {
    display: "flex",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dce8f7",
    boxShadow: "0 4px 24px rgba(0,40,90,0.07)",
    overflow: "hidden",
    maxWidth: 960,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "row",
      flexWrap: "nowrap",
    },
  },
  statBlock: {
    flex: 1,
    textAlign: "center",
    padding: "30px 18px",
    borderRight: "1px solid #dce8f7",
    [theme.breakpoints.down("md")]: {
      padding: "24px 13px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "20px 14px",
    },
    [theme.breakpoints.down("xs")]: {
      flex: "1 1 0",
      minWidth: 0,
      borderRight: "1px solid #dce8f7",
      borderBottom: "none",
      "&:last-child": {
        borderRight: "none",
      },
    },
  },
  statNum: {
    color: "#0057a8",
    fontWeight: 800,
    fontSize: LANDING_SHARED_TYPOGRAPHY.statValueDesktop,
    lineHeight: 1,
    marginBottom: 6,
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.statValueTablet,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.statValueMobile,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.statValueLaptop,
    },
  },
  statLabel: {
    color: "#3d5a80",
    fontSize: LANDING_SHARED_TYPOGRAPHY.statLabelDesktop,
    lineHeight: 1.4,
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.statLabelTablet,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.statLabelLaptop,
      lineHeight: 1.24,
    },
  },
  sectionAlt: {
    background: "linear-gradient(135deg, #f0f5fc 0%, #e8eef7 100%)",
    padding: "80px 0",
    [theme.breakpoints.down("md")]: {
      padding: "61px 0",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "43px 0",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "36px 0",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "72px 0",
    },
  },
  section: {
    padding: "0 6%",
    maxWidth: 1400,
    margin: "0 auto",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      padding: "0 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "0 18px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "0 12px",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "0 5%",
      maxWidth: 1320,
    },
  },
  sectionLabel: {
    color: "#0057a8",
    fontWeight: 700,
    fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextDesktop,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  sectionTitle: {
    color: "#001e3c",
    fontWeight: 700,
    fontSize: "clamp(1.6rem, 3vw, 2.3rem)",
    lineHeight: 1.25,
    marginBottom: 14,
    maxWidth: 700,
    [theme.breakpoints.down("md")]: {
      fontSize: "clamp(1.3rem, 2.35vw, 1.8rem)",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "clamp(1.3rem, 2.2vw, 1.82rem)",
      maxWidth: 560,
    },
  },
  sectionSub: {
    color: "#3d5a80",
    fontSize: "1.05rem",
    lineHeight: 1.7,
    maxWidth: 620,
    marginBottom: 48,
    [theme.breakpoints.down("md")]: {
      fontSize: "0.92rem",
      marginBottom: 32,
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 28,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "0.88rem",
      marginBottom: 24,
      maxWidth: 500,
    },
  },
  centeredSectionLabel: {
    textAlign: "center",
  },
  centeredSectionTitle: {
    textAlign: "center",
    margin: "0 auto 14px",
  },
  centeredSectionSub: {
    textAlign: "center",
    margin: "0 auto 36px",
  },
  audienceToggle: {
    display: "flex",
    justifyContent: "center",
    gap: 0,
    background: "#e8eef7",
    borderRadius: 14,
    padding: 5,
    maxWidth: 420,
    margin: "0 auto 42px",
    [theme.breakpoints.down("md")]: {
      maxWidth: 350,
      marginBottom: 32,
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
    [theme.breakpoints.down("xs")]: {
      padding: 4,
      marginBottom: 28,
    },
  },
  audienceTab: {
    flex: 1,
    fontWeight: 700,
    fontSize: LANDING_SHARED_BUTTONS.desktopFontSize,
    padding: "11px 22px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    transition: "background 0.2s, color 0.2s, box-shadow 0.2s",
    background: "transparent",
    color: "#3d5a80",
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_BUTTONS.tabletFontSize,
      padding: "9px 16px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "11px 16px",
      fontSize: LANDING_SHARED_BUTTONS.mobileFontSize,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_BUTTONS.laptopFontSize,
      padding: "7px 14px",
    },
  },
  audienceTabActive: {
    background: "#fff",
    color: "#001e3c",
    boxShadow: "0 2px 12px rgba(0,40,90,0.1)",
  },
  reveal: {
    opacity: 0,
    transform: "translateY(28px)",
    transition: "opacity 0.65s cubic-bezier(.2,.9,.2,1), transform 0.65s cubic-bezier(.2,.9,.2,1)",
  },
  revealVisible: {
    opacity: 1,
    transform: "none",
  },
  stepCardShell: {
    height: "100%",
    display: "flex",
    width: "100%",
  },
  stepCard: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dce8f7",
    padding: "32px 28px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,40,90,0.06)",
    transition: "transform 0.2s",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 485,
    [theme.breakpoints.down("md")]: {
      minHeight: 380,
      padding: "25px 20px",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "24px 18px",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      minHeight: 362,
      padding: "22px 18px",
    },
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  stepNum: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#0057a8",
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    [theme.breakpoints.down("md")]: {
      width: 40,
      height: 40,
      fontSize: "1rem",
      marginBottom: 14,
    },
  },
  stepImage: {
    width: "100%",
    borderRadius: 14,
    marginBottom: 20,
    display: "block",
    [theme.breakpoints.down("xs")]: {
      marginBottom: 16,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      marginBottom: 10,
    },
  },
  stepTitle: {
    color: "#001e3c",
    fontWeight: 700,
    fontSize: "clamp(1rem, 1.4vw, 1.1rem)",
    marginBottom: 10,
    [theme.breakpoints.down("md")]: {
      fontSize: "clamp(0.92rem, 1.2vw, 0.98rem)",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "clamp(0.9rem, 1vw, 0.96rem)",
    },
  },
  stepText: {
    color: "#3d5a80",
    fontSize: "0.94rem",
    lineHeight: 1.65,
    flex: 1,
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.sectionBodyTablet,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.sectionBodyLaptop,
      lineHeight: 1.45,
    },
  },
  ctaCenter: {
    textAlign: "center",
    marginTop: 48,
    [theme.breakpoints.down("md")]: {
      marginTop: 32,
    },
  },
  ctaButtonRow: {
    display: "inline-flex",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "nowrap",
    },
  },
  ctaFinePrint: {
    color: "#3d5a80",
    fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextDesktop,
    marginTop: 12,
  },
  teaserOuter: {
    padding: "80px 6%",
    [theme.breakpoints.down("md")]: {
      padding: "61px 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "43px 14px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "36px 12px",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "58px 3.25%",
    },
  },
  teaser: {
    background: "linear-gradient(135deg, #001e3c 0%, #003870 100%)",
    borderRadius: 18,
    padding: "52px 52px",
    display: "flex",
    gap: 48,
    alignItems: "center",
    maxWidth: 1200,
    margin: "0 auto",
    boxShadow: "0 24px 64px rgba(0,30,60,0.25)",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      padding: "32px 25px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: 20,
    },
    [theme.breakpoints.down("xs")]: {
      padding: 16,
      gap: 22,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "30px 30px",
      gap: 24,
    },
  },
  teaserText: {
    flex: 1,
  },
  teaserBadge: {
    color: "#fb741a",
    fontWeight: 700,
    fontSize: LANDING_SHARED_TYPOGRAPHY.smallTextDesktop,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  teaserTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "clamp(1.4rem, 2.5vw, 2.1rem)",
    lineHeight: 1.25,
    marginBottom: 16,
    [theme.breakpoints.down("md")]: {
      fontSize: "clamp(1.2rem, 2vw, 1.62rem)",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "clamp(1.12rem, 1.9vw, 1.52rem)",
    },
  },
  teaserBody: {
    color: "#b8d4f0",
    fontSize: LANDING_SHARED_TYPOGRAPHY.sectionBodyDesktop,
    lineHeight: 1.7,
    marginBottom: 28,
    [theme.breakpoints.down("md")]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.sectionBodyTablet,
      marginBottom: 20,
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: LANDING_SHARED_TYPOGRAPHY.sectionBodyLaptop,
      marginBottom: 16,
    },
  },
  stepPills: {
    display: "flex",
    gap: 10,
    marginBottom: 28,
    flexWrap: "wrap",
  },
  stepPill: {
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 100,
    padding: "6px 16px",
    color: "#c8dff7",
    fontSize: "0.84rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  stepPillNum: {
    background: "#fb741a",
    color: "#fff",
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.73rem",
    fontWeight: 700,
  },
  teaserImageWrap: {
    flex: "0 0 380px",
    maxWidth: 380,
    [theme.breakpoints.down("md")]: {
      flex: "none",
      width: "100%",
      maxWidth: "90%",
      margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "88%",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },
  teaserImage: {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: 14,
    boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
  },
  featureCard: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dce8f7",
    boxShadow: "0 4px 24px rgba(0,40,90,0.07)",
    padding: "36px 40px",
    display: "flex",
    gap: 36,
    alignItems: "flex-start",
    marginBottom: 24,
    transition: "transform 0.2s, box-shadow 0.2s",
    [theme.breakpoints.down("md")]: {
      padding: "25px 20px",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column !important",
      alignItems: "stretch",
      padding: "16px 14px",
      gap: 18,
    },
    [theme.breakpoints.down("sm")]: {
      gap: 21,
    },
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0,40,90,0.12)",
    },
  },
  featureImageWrap: {
    flex: "0 0 auto",
    width: "55%",
    maxWidth: 560,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("md")]: {
      width: "92%",
      maxWidth: 500,
      margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
      width: "90%",
      maxWidth: 460,
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      maxWidth: "100%",
      margin: "0 auto",
    },
  },
  featureImage: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  featureText: {
    flex: 1,
    minWidth: 0,
  },
  featureTag: {
    display: "inline-block",
    background: "#e8f0fc",
    color: "#0057a8",
    fontSize: "0.73rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderRadius: 100,
    padding: "4px 14px",
    marginBottom: 16,
    [theme.breakpoints.down("md")]: {
      marginBottom: 11,
    },
  },
  featureTitle: {
    color: "#001e3c",
    fontWeight: 700,
    fontSize: "clamp(1.05rem, 2vw, 1.35rem)",
    lineHeight: 1.3,
    marginBottom: 18,
    [theme.breakpoints.down("md")]: {
      fontSize: "0.95rem",
      marginBottom: 13,
    },
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 22px",
  },
  featureListItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 12,
    color: "#3d5a80",
    fontSize: "0.96rem",
    lineHeight: 1.65,
    [theme.breakpoints.down("md")]: {
      fontSize: "0.85rem",
      marginBottom: 9,
    },
  },
  checkmark: {
    color: "#0057a8",
    fontWeight: 700,
    fontSize: "0.95rem",
    flexShrink: 0,
    marginTop: 2,
  },
  featureCta: {
    color: "#fb741a",
    fontWeight: 700,
    fontSize: "0.9rem",
    border: "none",
    background: "none",
    padding: 0,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  bonusCard: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dce8f7",
    padding: "28px 30px",
    boxShadow: "0 4px 20px rgba(0,40,90,0.06)",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    [theme.breakpoints.down("md")]: {
      padding: "22px 20px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "24px 20px",
    },
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 10px 28px rgba(0,40,90,0.1)",
    },
  },
  bonusIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #e8f0fc, #d5e3f7)",
    fontSize: "1.5rem",
    marginBottom: 16,
  },
  bonusTitle: {
    color: "#001e3c",
    fontWeight: 700,
    fontSize: "1.05rem",
    lineHeight: 1.35,
    marginBottom: 10,
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "0.98rem",
    },
  },
  bonusText: {
    color: "#3d5a80",
    fontSize: "0.94rem",
    lineHeight: 1.7,
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "0.9rem",
      lineHeight: 1.65,
    },
  },
  bonusCtaWrap: {
    textAlign: "center",
    marginTop: 46,
  },
  bonusCtaLead: {
    color: "#3d5a80",
    fontSize: "0.98rem",
    lineHeight: 1.6,
    maxWidth: 640,
    margin: "0 auto 22px",
  },
  screensOuter: {
    padding: "80px 6%",
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      padding: "61px 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "43px 14px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "36px 12px",
    },
  },
  screensWrap: {
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(0,30,60,0.18)",
    border: "1px solid #dce8f7",
    maxWidth: 1100,
    margin: "0 auto 40px",
  },
  screensImage: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  trustBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 40,
    flexWrap: "wrap",
    padding: "48px 6%",
    background: "#f7faff",
    borderTop: "1px solid #dce8f7",
    [theme.breakpoints.down("md")]: {
      gap: 25,
      padding: "36px 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      gap: 21,
      padding: "29px 14px",
    },
    [theme.breakpoints.down("xs")]: {
      gap: 13,
      padding: "22px 12px",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      gap: 32,
      padding: "42px 5%",
    },
  },
  trustBarImage: {
    height: 52,
    width: "auto",
    opacity: 0.85,
  },
  finalCta: {
    background: "linear-gradient(135deg, #001e3c 0%, #0057a8 100%)",
    padding: "96px 6%",
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      padding: "68px 4.5%",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "50px 14px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "43px 12px",
    },
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      padding: "84px 5%",
    },
  },
  finalTitle: {
    color: "#fff",
    fontWeight: 800,
    fontSize: "clamp(1.8rem, 3.5vw, 2.9rem)",
    lineHeight: 1.2,
    margin: "0 auto 20px",
    maxWidth: 720,
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "clamp(1.65rem, 3vw, 2.6rem)",
      maxWidth: 660,
    },
  },
  finalBody: {
    color: "#b8d4f0",
    fontSize: "1.05rem",
    lineHeight: 1.7,
    maxWidth: 560,
    margin: "0 auto 40px",
    ["@media (min-width: 1200px) and (max-width: 1499px)"]: {
      fontSize: "0.98rem",
      maxWidth: 520,
      marginBottom: 32,
    },
  },
  finalButtons: {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: 18,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      flexWrap: "nowrap",
    },
  },
  finalFinePrint: {
    color: "#7ba7d4",
    fontSize: "0.83rem",
  },
  divider: {
    width: "90%",
    margin: "0 auto",
  },
  albumWrap: {
    background: "linear-gradient(90deg, #dce4eb, #d7d4cf)",
    paddingBottom: 16,
  },
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 0.4,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.65,
      transform: "scale(1.08)",
    },
  },
});

function Maincontent({
  bookingDialogOpen,
  bookingDialogPath = "assessment_first",
  onCloseBookingDialog,
  onOpenBookingDialog,
  onShowClinicContent,
  classes,
}) {
  const [revealed, setRevealed] = React.useState({});
  const [audience, setAudience] = React.useState("patient");
  const [heroVideoReady, setHeroVideoReady] = React.useState(false);
  const [heroVideoFailed, setHeroVideoFailed] = React.useState(false);
  const [localBookingDialogOpen, setLocalBookingDialogOpen] = React.useState(false);
  const [localBookingDialogPath, setLocalBookingDialogPath] = React.useState("assessment_first");
  const [useCompactStatsLabels, setUseCompactStatsLabels] = React.useState(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(max-width: 959px)").matches;
    }

    return false;
  });
  const [useXsStatsLabels, setUseXsStatsLabels] = React.useState(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(max-width: 599px)").matches;
    }

    return false;
  });
  const [findClinicLabel, setFindClinicLabel] = React.useState(() => getFindClinicLabel());
  const [startIntakeLabel, setStartIntakeLabel] = React.useState(() => getStartIntakeLabel());
  const revealRefs = React.useRef({});
  const revealObserverRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 959px)");
    const updateLabels = () => setUseCompactStatsLabels(mediaQuery.matches);

    updateLabels();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLabels);
      return () => mediaQuery.removeEventListener("change", updateLabels);
    }

    mediaQuery.addListener(updateLabels);
    return () => mediaQuery.removeListener(updateLabels);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 599px)");
    const updateLabels = () => setUseXsStatsLabels(mediaQuery.matches);

    updateLabels();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLabels);
      return () => mediaQuery.removeEventListener("change", updateLabels);
    }

    mediaQuery.addListener(updateLabels);
    return () => mediaQuery.removeListener(updateLabels);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 960px)");
    const updateLabel = () => setFindClinicLabel(mediaQuery.matches ? "Find a clinic near you" : "Find clinic");

    updateLabel();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLabel);
      return () => mediaQuery.removeEventListener("change", updateLabel);
    }

    mediaQuery.addListener(updateLabel);
    return () => mediaQuery.removeListener(updateLabel);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 960px)");
    const updateLabel = () => setStartIntakeLabel(mediaQuery.matches ? "Start Digital Intake" : "START INTAKE");

    updateLabel();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLabel);
      return () => mediaQuery.removeEventListener("change", updateLabel);
    }

    mediaQuery.addListener(updateLabel);
    return () => mediaQuery.removeListener(updateLabel);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const revealId = entry.target.getAttribute("data-reveal-id");
        if (revealId) {
          setRevealed((previous) => ({ ...previous, [revealId]: true }));
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    revealObserverRef.current = observer;

    Object.values(revealRefs.current).forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      revealObserverRef.current = null;
      observer.disconnect();
    };
  }, []);

  const dialogOpen = typeof bookingDialogOpen === "boolean" ? bookingDialogOpen : localBookingDialogOpen;
  const dialogPath = typeof bookingDialogOpen === "boolean" ? bookingDialogPath : localBookingDialogPath;

  const handleOpenBookingDialog = (path = "assessment_first") => {
    if (onOpenBookingDialog) {
      onOpenBookingDialog(path);
      return;
    }

    setLocalBookingDialogPath(path);
    setLocalBookingDialogOpen(true);
  };

  const handleCloseBookingDialog = () => {
    if (onCloseBookingDialog) {
      onCloseBookingDialog();
      return;
    }

    setLocalBookingDialogOpen(false);
  };

  const handleShowClinicContent = () => {
    if (onShowClinicContent) {
      onShowClinicContent();
    }
  };

  const setRevealRef = (id) => (element) => {
    const previousElement = revealRefs.current[id];
    if (previousElement && revealObserverRef.current) {
      revealObserverRef.current.unobserve(previousElement);
    }

    if (!element) {
      delete revealRefs.current[id];
      return;
    }

    revealRefs.current[id] = element;
    element.setAttribute("data-reveal-id", id);

    if (!revealed[id] && revealObserverRef.current) {
      revealObserverRef.current.observe(element);
    }
  };

  const getRevealClassName = (id) => [
    classes.reveal,
    revealed[id] ? classes.revealVisible : "",
  ].filter(Boolean).join(" ");

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12} className={classes.heroSection}>
        <div className={classes.heroOrb} />
        <div className={classes.heroInner}>
          <Grid container alignItems="flex-start" spacing={4}>
            <Grid item xs={12} sm={6} md={6} className={classes.heroCopyWrap}>
              {/*<div className={classes.heroBadge}>Hybrid physiotherapy - in-clinic and at home</div>*/}
              <Typography component="h1" className={classes.heroHeadline}>
                Better physiotherapy starts <span className={classes.heroHeadlineAccent}>before the first appointment</span>.
              </Typography>
              <Typography className={classes.heroSub}>
                VirtusPhysio connects you to the right physiotherapist, lets you complete a digital intake from home, and keeps your rehabilitation on track between clinic visits so every in-person visit or videoconsultation counts.
              </Typography>
              <div className={classes.dualCta}>
                <Button variant="contained" className={classes.primaryButton} onClick={goToAssessment}>
                  {startIntakeLabel}
                </Button>
                <Button variant="outlined" className={classes.outlineLightButton} onClick={() => handleOpenBookingDialog("booking_first")}>
                  {findClinicLabel}
                </Button>
              </div>
              <Typography className={classes.heroHint}>
                Not ready to book? Complete the intake now and answer the rest before your appointment.
              </Typography>
              <div className={classes.trustBadges}>
                {/*<span className={classes.trustLabel}>Compliant and secure</span>*/}
                <img src={GDPRcompliant_logoGrey} alt="GDPR compliant" className={classes.trustBadgeImage} />
                <img src={DanishHealth} alt="Danish Health Authority" className={classes.trustBadgeImage} />
                {/*<img src={CEMark} alt="CE mark" className={classes.trustBadgeImageLarge} />*/}
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6} className={classes.heroIllustrationWrap}>
              <div className={classes.heroMediaShell}>
                <div
                  className={classes.heroMediaFallback}
                  style={{ opacity: heroVideoReady && !heroVideoFailed ? 0.14 : 1 }}
                >
                  <img src={hybridPhysio4148919.png} alt="Hybrid physiotherapy illustration" className={classes.heroIllustration} />
                </div>
                {!heroVideoFailed && (
                  <video
                    className={classes.heroVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={hybridPhysio4148919.png}
                    aria-hidden="true"
                    onCanPlay={() => setHeroVideoReady(true)}
                    onError={() => setHeroVideoFailed(true)}
                  >
                    <source src={HERO_VIDEO_WEBM_SRC} type="video/webm" />
                    <source src={HERO_VIDEO_MP4_SRC} type="video/mp4" />
                  </video>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </Grid>

      <Grid item xs={12} className={classes.statsOuter}>
        <div className={classes.statsBar}>
          {stats.map((stat, index) => (
            <div key={stat.label} className={classes.statBlock}>
              <div className={classes.statNum}>{stat.value}</div>
              <div className={classes.statLabel}>{useXsStatsLabels ? xsStatsLabels[index] : useCompactStatsLabels ? compactStatsLabels[index] : stat.label}</div>
            </div>
          ))}
        </div>
      </Grid>

      <Grid item xs={12} className={classes.sectionAlt} id="landing-how-it-works">
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>How it works</Typography>
          <Typography component="h2" className={classes.sectionTitle}>
            From intake to recovery, a joined-up journey
          </Typography>
          <Typography className={classes.sectionSub}>
            Tell us about yourself, get matched to the right physiotherapist, and stay on track between appointments in one connected flow.
          </Typography>
          <Grid container spacing={3}>
            {howItWorksSteps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.id}>
                <div
                  className={classes.stepCardShell}
                >
                  <div
                  className={getRevealClassName(step.id)}
                  ref={setRevealRef(step.id)}
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  <div className={classes.stepCard}>
                    <div className={classes.stepNum}>{step.number}</div>
                    <img src={step.image} alt={step.alt} className={classes.stepImage} />
                    <div className={classes.stepTitle}>{step.title}</div>
                    <div className={classes.stepText}>{step.text}</div>
                  </div>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
          <div className={classes.ctaCenter}>
            <div className={classes.ctaButtonRow}>
                <Button variant="contained" className={classes.primaryButton} onClick={goToAssessment}>
                  {startIntakeLabel}
              </Button>
              <Button variant="outlined" className={classes.outlineBlueButton} onClick={() => handleOpenBookingDialog("booking_first")}>
                {findClinicLabel}
              </Button>
            </div>
            <div className={classes.ctaFinePrint}>Free intake - no credit card - complete now or before your appointment</div>
          </div>
        </div>
      </Grid>

      <Grid item xs={12} className={classes.teaserOuter}>
        <div className={getRevealClassName("teaser")} ref={setRevealRef("teaser")}>
          <div className={classes.teaser}>
            <div className={classes.teaserText}>
              <div className={classes.teaserBadge}>Takes less than 5 minutes</div>
              <div className={classes.teaserTitle}>Your physiotherapist will know you before you arrive</div>
              <div className={classes.teaserBody}>
                The digital intake is a simple guided questionnaire with no medical jargon and no long forms. You describe your concern in your own words, and the clinic receives what it needs before the first session starts.
              </div>
              <div className={classes.stepPills}>
                {["Your concern", "Daily impact", "Your goals", "Matched to a physio"].map((pill, index) => (
                  <div key={pill} className={classes.stepPill}>
                    <span className={classes.stepPillNum}>{index + 1}</span>
                    {pill}
                  </div>
                ))}
              </div>
              <div className={classes.dualCta}>
                <Button variant="contained" className={classes.primaryButton} onClick={goToAssessment}>
                  {startIntakeLabel}
                </Button>
                <Button variant="outlined" className={classes.outlineLightButton} onClick={() => handleOpenBookingDialog("booking_first")}>
                  {findClinicLabel}
                </Button>
              </div>
            </div>
            <div className={classes.teaserImageWrap}>
              <img src={Assessment2} alt="Digital intake preview" className={classes.teaserImage} />
            </div>
          </div>
        </div>
      </Grid>

      <Grid item xs={12} className={classes.sectionAlt}>
        <div className={classes.section}>
          <Typography className={`${classes.sectionLabel} ${classes.centeredSectionLabel}`}>Platform features</Typography>
          <Typography component="h2" className={`${classes.sectionTitle} ${classes.centeredSectionTitle}`}>
            Built for patients, clinics, and insurance-supported pathways
          </Typography>
          <Typography className={`${classes.sectionSub} ${classes.centeredSectionSub}`}>
            VirtusPhysio works best when both sides stay connected. Read about how we help the patient journey and the clinics at the same time.
          </Typography>
          <div className={classes.audienceToggle}>
            {[
              { value: "patient", label: "For patients" },
              { value: "physio", label: "For physios & insurances" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={[
                  classes.audienceTab,
                  audience === option.value ? classes.audienceTabActive : "",
                ].filter(Boolean).join(" ")}
                onClick={() => setAudience(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {audience === "patient" && patientFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className={getRevealClassName(feature.id)}
              ref={setRevealRef(feature.id)}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className={classes.featureCard} style={{ flexDirection: index % 2 === 0 ? "row" : "row-reverse" }}>
                <div className={classes.featureImageWrap}>
                  <img src={feature.image} alt={feature.alt} className={classes.featureImage} />
                </div>
                <div className={classes.featureText}>
                  <span className={classes.featureTag}>{feature.tag}</span>
                  <div className={classes.featureTitle}>{feature.title}</div>
                  <ul className={classes.featureList}>
                    {feature.bullets.map((bullet) => (
                      <li key={bullet} className={classes.featureListItem}>
                        <span className={classes.checkmark}>+</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className={classes.featureCta} onClick={goToAssessment}>{feature.ctaLabel} -&gt;</button>
                </div>
              </div>
            </div>
          ))}

          {audience === "physio" && (
            <React.Fragment>
              <Grid container spacing={3}>
                {physioBonuses.map((bonus, index) => (
                  <Grid item xs={12} sm={6} md={4} key={bonus.id} style={{ display: "flex" }}>
                    <div
                      className={getRevealClassName(bonus.id)}
                      ref={setRevealRef(bonus.id)}
                      style={{ transitionDelay: `${index * 80}ms`, display: "flex", width: "100%" }}
                    >
                      <div className={classes.bonusCard}>
                        <div className={classes.bonusIcon} aria-hidden="true">{bonus.icon}</div>
                        <div className={classes.bonusTitle}>{bonus.title}</div>
                        <div className={classes.bonusText}>{bonus.text}</div>
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
              <div className={classes.bonusCtaWrap}>
                <div className={classes.bonusCtaLead}>
                  Read more about the full clinic experience in our dedicated physio and insurance section. Use the button below to switch there directly.
                </div>
                <Button variant="contained" className={classes.primaryButton} onClick={handleShowClinicContent}>
                  Get your clinic listed
                </Button>
              </div>
            </React.Fragment>
          )}
        </div>
      </Grid>

      <Grid item xs={12} className={classes.screensOuter}>
        <Typography className={`${classes.sectionLabel} ${classes.centeredSectionLabel}`}>Patient app and platform</Typography>
        <Typography component="h2" className={`${classes.sectionTitle} ${classes.centeredSectionTitle}`}>
          Rehabilitation that goes home with you
        </Typography>
        <Typography className={`${classes.sectionSub} ${classes.centeredSectionSub}`}>
          Your home exercise plan, weekly check-ins, progress tracking, and direct access to your physiotherapist can live in one experience across devices.
        </Typography>
        {/*<div className={getRevealClassName("screens")} ref={setRevealRef("screens")}>
          <div className={classes.screensWrap}>
            <img src={Screens} alt="VirtusPhysio patient app and physio dashboard" className={classes.screensImage} />
          </div>
        </div>*/}
        <div className={classes.ctaButtonRow}>
          <Button variant="contained" className={classes.primaryButton} onClick={goToAssessment}>
            {startIntakeLabel}
          </Button>
          <Button variant="outlined" className={classes.outlineBlueButton} onClick={() => handleOpenBookingDialog("booking_first")}>
            {findClinicLabel}
          </Button>
        </div>
      </Grid>

      <Grid item xs={12} className={classes.trustBar}>
        <span className={classes.trustLabel} style={{ color: "#3d5a80" }}>Secure and compliant</span>
        <img src={GDPRcompliant_logoGrey} alt="GDPR compliant" className={classes.trustBarImage} />
        <img src={DanishHealth} alt="Danish Health Authority" className={classes.trustBarImage} />
        <img src={CEMark} alt="CE mark" className={classes.trustBarImage} />
      </Grid>

      <Grid item xs={12} className={classes.finalCta}>
        <Typography component="h2" className={classes.finalTitle}>
          Ready to make every physio appointment count?
        </Typography>
        <Typography className={classes.finalBody}>
          Start your digital intake in minutes, or find a VirtusPhysio clinic near you and book your first appointment. You can always complete the intake before you go.
        </Typography>
        <div className={classes.finalButtons}>
          <Button variant="contained" className={classes.primaryButton} onClick={goToAssessment}>
            Start Digital Intake
          </Button>
          <Button variant="outlined" className={classes.outlineLightButton} onClick={() => handleOpenBookingDialog("booking_first")}>
            Find a clinic near you
          </Button>
        </div>
        <div className={classes.finalFinePrint}>Free intake - GDPR compliant - no commitment required</div>
      </Grid>

      <Divider variant="middle" className={classes.divider} />

      <Grid item xs={12} className={classes.albumWrap}>
        <AlbumSymptoms />
      </Grid>

      <TemporaryClinicBookingDialog
        open={dialogOpen}
        initialPath={dialogPath}
        onClose={handleCloseBookingDialog}
      />
    </Grid>
  );
}

export default withStyles(styles)(Maincontent);